import React, { useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment/moment';
import lodashCompact from 'lodash/fp/compact';
import lodashXor from 'lodash/fp/xor';

import { DATA_TABLE_SOURCE_UTILITY_PARTNER_ADDRESSES } from 'common/dataTableConstants';

import {
  PARTNER_ADDRESS_STATUS_LISTED,
  PARTNER_ADDRESS_STATUS_UNLISTED,
  PARTNER_ADDRESS_STATUS_WITHDRAWN,
  PARTNER_ADDRESS_STATUS_LEASED,
  UTILITY_TYPE_ID_LIABILITY_INSURANCE,
} from 'common/utility/constants';

import { makeAddressCityStateString } from '@utilityprofit/address-helpers';
import makeFullName from 'common/util/makeFullName';
import dateStringMomentWithCurrentTime from 'common/util/dateStringMomentWithCurrentTime';

import {
  SR_API_ACTION_ADDRESS_UPDATE_REQUIRED_TYPES,
  SR_API_ACTION_ADDRESS_START_REMINDERS_FOR_RENTER,
  SR_API_ACTION_UTILITY_ACTIVATION_GET,
  SR_API_ACTION_UTILITY_PARTNER_ADDRESS_UPDATE_ADDRESS,
} from 'api/utilityActionTypes';

import requestUtilityApi from 'client/actions/requestUtilityApi';
import useDispatch from 'client/hooks/useDispatch';

import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrDataTableUI from 'client/components/SrDataTableUI';
import SrTooltip, { SR_TOOLTIP_POSITION_CENTER } from 'client/components/SrTooltip';
import { SR_TABLE_THEME_PARTNER_CLEAR } from 'client/components/SrTable';
import SrDataTableConnected from 'client/components/SrDataTableConnected';
import SrUtilityProfitNewAddressModal from 'client/components/SrUtilityProfit/SrUtilityProfitNewAddressModal';
import SrUtilityAddressRequiredTypesModal from 'client/components/SrUtilityAddressRequiredTypesModal/SrUtilityAddressRequiredTypesModal';
import SrUtilityProfitRenterContactInformationModal from 'client/components/SrUtilityProfit/SrUtilityProfitRenterContactInformationModal';
import SrUtilityProfitAddressesDataTableHeader from 'client/components/SrUtilityProfitAddressesDataTable/SrUtilityProfitAddressesDataTableHeader';
import SrSimpleMessageModal, {
  SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP,
} from 'client/components/SrSimpleMessageModal/SrSimpleMessageModal';
import {
  SR_BUTTON_THEME_ADOBE_GREY,
  SR_BUTTON_THEME_ADOBE_YELLOW,
} from 'client/components/SrButton/SrButton';

function getDataTableSettingsFromProps({ utilityPartnerId }) {
  const filter = {
    status: [
      PARTNER_ADDRESS_STATUS_LISTED,
      PARTNER_ADDRESS_STATUS_UNLISTED,
      PARTNER_ADDRESS_STATUS_WITHDRAWN,
      PARTNER_ADDRESS_STATUS_LEASED,
    ],
  };

  if (utilityPartnerId) {
    filter.utilityPartnerId = utilityPartnerId;
  }

  const viewId = `${DATA_TABLE_SOURCE_UTILITY_PARTNER_ADDRESSES}_${
    utilityPartnerId || 'no_partner_id'
  }`;

  const dataTableSettings = {
    dataSource: DATA_TABLE_SOURCE_UTILITY_PARTNER_ADDRESSES,
    rootViewId: viewId,
    initialViewSettings: {
      filter,
      sort: {
        createdAt: 'desc',
      },
    },
    count: 8,
    itemsPropName: 'addresses',
    resetOnUnmount: true,
  };
  return dataTableSettings;
}

const SrUtilityProfitAddressDataTableAddressCell = ({ address, showPartner }) => (
  <SrText font="greycliff" fontSize="16px" weight="400">
    <SrText weight="800">{address.streetDisplay}</SrText>
    {`  ${makeAddressCityStateString(address)}`}
    {showPartner && (
      <SrBox marginTop="-7px">
        <SrText size="12" weight="500">
          {address.partnerName}
        </SrText>
      </SrBox>
    )}
  </SrText>
);

SrUtilityProfitAddressDataTableAddressCell.propTypes = {
  address: PropTypes.object.isRequired,
};

SrUtilityProfitAddressDataTableAddressCell.displayName =
  'SrUtilityProfitAddressDataTableAddressCell';

const formatDate = (date) => {
  return moment(date).format('MMM D, YYYY');
};

const SrUtilityProfitAddressesDataTable = ({
  title,
  showPartner,
  utilityPartner,
  utilityPartnerSlug,
  utilityPartnerList,
  utilityTypes,
  liabilityInsuranceEnabled,
}) => {
  const utilityPartnerId = utilityPartner?.id;

  // Filter out liability insurance if not enabled in experience configuration
  const filteredUtilityTypes = utilityTypes.filter(
    (type) =>
      type.id !== UTILITY_TYPE_ID_LIABILITY_INSURANCE || liabilityInsuranceEnabled,
  );

  const [updatingAddressId, setUpdatingAddressId] = useState(null);
  const [newAddressModalIsOpen, setNewAddressModalIsOpen] = useState(false);
  const [selectingAddress, setSelectingAddress] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [renterContactInfoPendingConfirmation, setRenterContactInfoPendingConfirmation] = useState(
    null,
  );
  const dispatch = useDispatch();

  const dataTableHeaders = [
    {
      content: 'Address',
      style: { width: '300px' },
    },
    {
      content: 'Created',
      dataTableFieldId: 'createdAt',
      sortPosition: 'right',
    },
    {
      content: (
        <SrText weight="800">
          Utilities&nbsp;
          <SrText weight="400">
            (Enabled/<SrText color="orange300">Disabled</SrText>)
          </SrText>
        </SrText>
      ),
    },
    {
      content: '',
    },
    {
      content: '',
    },
  ];

  const onSearchInputChange = ({ loadNextPage, viewSettings, input }) => {
    const searchTerm = input.target.value;
    setSearchInput(searchTerm);
    if (viewSettings) {
      viewSettings.update({
        ...viewSettings,
        loadAllData: true,
        fuzzy: !searchTerm
          ? null
          : {
              queryString: searchTerm,
              columns: ['display'],
            },
        count: 50,
      });
      loadNextPage();
    }
  };

  return (
    <SrDataTableConnected {...getDataTableSettingsFromProps({ utilityPartnerId })}>
      {({
        addresses,
        reloadItems,
        viewSettings,
        dataTableApiState,
        loadNextPage,
        dataTableIsFullyLoaded,
      }) => {
        // eslint-disable-line max-len
        const updatingAddress = !updatingAddressId
          ? null
          : addresses.find(({ id }) => id === updatingAddressId);

        if (dataTableApiState.inProgress && addresses.length === 0) {
          return (
            <SrBox paddingBottom="40px">
              <SrUtilityProfitAddressesDataTableHeader
                title={title}
                searchInput={searchInput}
                onSearchInputChange={(input) =>
                  onSearchInputChange({ loadNextPage, viewSettings, input })
                }
                onAddAddress={() => setNewAddressModalIsOpen(true)}
              />
              <SrBox marginTop="20px" paddingBottom="20px">
                <div className="loading-ellipses clearfix" />
              </SrBox>
            </SrBox>
          );
        }

        const reloadByAddressId = (addressId) => {
          return reloadItems({
            count: 1,
            filter: {
              id: addressId,
            },
          });
        };

        const updateRequiredTypes = ({ utilityTypeIds, propertyManagerEmail }) => {
          dispatch(requestUtilityApi, {
            action: SR_API_ACTION_UTILITY_PARTNER_ADDRESS_UPDATE_ADDRESS,
            payload: {
              id: updatingAddress.partnerAddressId,
              updates: {
                propertyManagerEmail: propertyManagerEmail || null,
              },
            },
            onError(error) {
              global.alert(error.message);
            },
            onSuccess: () => {
              if (
                lodashXor(
                  utilityTypeIds,
                  updatingAddress.requiredTypes.map(({ id }) => id),
                ).length === 0
              ) {
                setUpdatingAddressId(null);
                return reloadByAddressId(updatingAddress.id);
              }
              dispatch(requestUtilityApi, {
                action: SR_API_ACTION_ADDRESS_UPDATE_REQUIRED_TYPES,
                payload: {
                  addressId: updatingAddress.id,
                  utilityTypeIds,
                },
                onError(error) {
                  global.alert(error.message);
                },
              })
                .then(() => {
                  return reloadByAddressId(updatingAddress.id);
                })
                .then(() => {
                  setUpdatingAddressId(null);
                });
            },
          });
        };

        const onSubmitRenterContactInformationForm = (formValues, cancelIsSubmitting) => {
          return dispatch(requestUtilityApi, {
            action: SR_API_ACTION_UTILITY_ACTIVATION_GET,
            payload: {
              addressId: selectingAddress.id,
            },
          }).then((activation) => {
            if (
              activation &&
              (!selectingAddress.renterReminderSentUser ||
                (selectingAddress.renterReminderSentUser.phone !== formValues.phone &&
                  selectingAddress.renterReminderSentUser.email !== formValues.email))
            ) {
              cancelIsSubmitting();
              setRenterContactInfoPendingConfirmation(formValues);
            } else {
              return onConfirmRenterContactInformationForm(formValues);
            }
          });
        };

        const onConfirmRenterContactInformationForm = (formValues) => {
          setRenterContactInfoPendingConfirmation(null);
          return dispatch(requestUtilityApi, {
            action: SR_API_ACTION_ADDRESS_START_REMINDERS_FOR_RENTER,
            payload: {
              utilityPartnerAddressId: selectingAddress.partnerAddressId,
              leaseStartDate: formValues.leaseStartDate,
              user: {
                id: formValues.email,
                firstName: formValues.firstName,
                lastName: formValues.lastName,
                email: formValues.email,
                phone: formValues.phone,
              },
            },
            onError(error) {
              global.alert(error.message);
            },
          })
            .then(() => {
              return reloadByAddressId(selectingAddress.id);
            })
            .then(() => {
              setSelectingAddress(null);
            });
        };

        const addressRows = addresses.map((address) => {
          return {
            onClick: () => setUpdatingAddressId(address.id),
            cells: [
              {
                content: (
                  <SrUtilityProfitAddressDataTableAddressCell
                    address={address}
                    showPartner={showPartner}
                  />
                ),
                style: {
                  maxWidth: '290px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                },
              },
              {
                content: formatDate(address.createdAt),
                style: {
                  fontWeight: '400',
                },
              },
              {
                content: (
                  <SrText font="greycliff">
                    {filteredUtilityTypes.map(({ id: utilityTypeId }) => {
                      const isRequired = address.requiredTypes?.some((requiredType) => {
                        return requiredType.id === utilityTypeId;
                      });
                      const requiredType = filteredUtilityTypes.find(({ id }) => id === utilityTypeId);
                      const displayLabel = utilityTypeId === UTILITY_TYPE_ID_LIABILITY_INSURANCE ? 'Insurance' : requiredType.label;
                      return (
                        <SrText color={!isRequired ? 'orange300' : ''}>
                          {displayLabel}
                          {utilityTypeId !== filteredUtilityTypes[filteredUtilityTypes.length - 1].id && ', '}
                        </SrText>
                      );
                    })}
                  </SrText>
                ),
                style: {
                  fontWeight: '400',
                },
              },
              {
                onClick: (event) => {
                  event.stopPropagation();
                  setSelectingAddress(address);
                },
                content: (
                  <SrBox float="right">
                    {address.renterReminderStartedAt &&
                      (address.renterReminderSentUser ? (
                        <SrTooltip
                          position={SR_TOOLTIP_POSITION_CENTER}
                          bgColor="blue"
                          icon={<span />}
                          tooltipContentStyle={{
                            transform: 'translateX(0)',
                          }}
                          label={
                            <SrText
                              font="greycliff"
                              weight="500"
                              paddingRight="20px"
                              underlineOnHover
                            >
                              Reminders started!
                            </SrText>
                          }
                        >
                          {makeFullName(
                            address.renterReminderSentUser.firstName,
                            address.renterReminderSentUser.lastName,
                          ) +
                            '\n' +
                            (address.renterReminderSentUser?.leaseStartDate
                              ? dateStringMomentWithCurrentTime(
                                  address.renterReminderSentUser.leaseStartDate,
                                ).format('MM/DD/YYYY')
                              : '')}
                        </SrTooltip>
                      ) : (
                        <SrText font="greycliff" weight="500" paddingRight="20px" underlineOnHover>
                          Reminders started!
                        </SrText>
                      ))}

                    {!address.renterReminderStartedAt && (
                      <SrText font="greycliff" weight="700" color="green" paddingRight="20px">
                        Send renter reminders
                      </SrText>
                    )}
                  </SrBox>
                ),
              },
              {
                content: (
                  <SrBox float="right">
                    <SrText font="greycliff" color="blue300" size="16" weight="700">
                      Edit
                    </SrText>
                  </SrBox>
                ),
              },
            ],
          };
        });

        const loadMoreRow =
          dataTableIsFullyLoaded || viewSettings?.fuzzy?.queryString
            ? null
            : {
                onClick: () => (dataTableApiState.inProgress ? null : loadNextPage()),
                cells: [
                  {
                    content: (
                      <div
                        style={{ height: '100%', textAlign: 'center', backgroundColor: '#F5F9FF' }}
                      >
                        <SrText
                          lineHeight="50px"
                          font="greycliff"
                          weight="800"
                          size="16"
                          color="blue300"
                        >
                          View more listings
                        </SrText>
                      </div>
                    ),
                    colSpan: dataTableHeaders.length,
                    noPadding: true,
                  },
                ],
              };

        return (
          <SrBox>
            <SrUtilityProfitAddressesDataTableHeader
              title={title}
              searchInput={searchInput}
              onSearchInputChange={(input) =>
                onSearchInputChange({ loadNextPage, viewSettings, input })
              }
              onAddAddress={() => setNewAddressModalIsOpen(true)}
            />
            <SrBox marginTop="20px">
              <SrDataTableUI
                sticky
                theme={SR_TABLE_THEME_PARTNER_CLEAR}
                dataTableViewSettings={viewSettings}
                headers={dataTableHeaders}
                minWidth="100%"
                rows={lodashCompact([...addressRows, loadMoreRow])}
              />
              {dataTableApiState.inProgress && (
                <div>
                  <div className="loading-ellipses clearfix" />
                </div>
              )}
            </SrBox>
            {addresses.length === 0 && dataTableIsFullyLoaded && (
              <SrBox backgroundColor="white" display="flex" justifyContent="center" padding="20px">
                <SrText font="greycliff" weight="500" fontSize="16px" fontStyle="italic">
                  {searchInput ? 'No search results.' : 'No listings added yet.'}
                </SrText>
              </SrBox>
            )}
            {updatingAddress && (
              <SrUtilityAddressRequiredTypesModal
                address={updatingAddress}
                initialUtilityTypeIds={updatingAddress.requiredTypes.map(({ id }) => id)}
                utilityPartner={utilityPartner}
                utilityPartnerSlug={utilityPartnerSlug}
                utilityProviders={updatingAddress.utilityProviders}
                utilityTypes={utilityTypes}
                requiredTypes={updatingAddress.requiredTypes}
                onClose={() => setUpdatingAddressId(null)}
                onSubmit={updateRequiredTypes}
                onAddressUpdate={reloadByAddressId}
                liabilityInsuranceEnabled={liabilityInsuranceEnabled}
                dispatch={dispatch}
              />
            )}
            {newAddressModalIsOpen && (
              <SrUtilityProfitNewAddressModal
                utilityPartnerId={utilityPartnerId}
                utilityPartnerList={utilityPartnerList}
                afterSubmit={(address) => {
                  setNewAddressModalIsOpen(false);
                  if (address) {
                    reloadByAddressId(address.id);
                  }
                }}
                onClose={() => setNewAddressModalIsOpen(false)}
              />
            )}
            {selectingAddress && (
              <SrUtilityProfitRenterContactInformationModal
                address={selectingAddress}
                onClose={() => setSelectingAddress(null)}
                onSubmit={onSubmitRenterContactInformationForm}
                initialValues={selectingAddress.renterReminderSentUser}
              />
            )}
            {renterContactInfoPendingConfirmation && (
              <SrSimpleMessageModal
                iconUrl="/images/question-mark.svg"
                maxIconHeight="50px"
                maxIconWidth="50px"
                contentContainerClassName="utilityProviderContact"
                iconBgColor="#FFFBEB"
                headline={
                  'This utility activation has already been started, are you ' +
                  'sure you want to restart it with the new tenant?'
                }
                underline
                maxWidth={400}
                message={
                  'By restarting, the current utility confirmations will disappear ' +
                  'and the new tenant will need to confirm them all again.'
                }
                theme={SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP}
                srMultiButtons={[
                  {
                    buttonText: 'Restart Utility Activations',
                    onButtonClick: () => {
                      onConfirmRenterContactInformationForm(renterContactInfoPendingConfirmation);
                    },
                    buttonTheme: SR_BUTTON_THEME_ADOBE_YELLOW,
                  },
                  {
                    buttonText: 'Cancel',
                    onButtonClick: () => setRenterContactInfoPendingConfirmation(null),
                    buttonTheme: SR_BUTTON_THEME_ADOBE_GREY,
                  },
                ]}
              />
            )}
          </SrBox>
        );
      }}
    </SrDataTableConnected>
  );
};

SrUtilityProfitAddressesDataTable.propTypes = {
  title: PropTypes.string,
  showPartner: PropTypes.bool,
  utilityPartner: PropTypes.object,
  utilityPartnerSlug: PropTypes.string,
  utilityPartnerList: PropTypes.array,
  utilityTypes: PropTypes.array.isRequired,
  liabilityInsuranceEnabled: PropTypes.bool,
};

SrUtilityProfitAddressesDataTable.displayName = 'SrUtilityProfitAddressesDataTable';

export default SrUtilityProfitAddressesDataTable;
